import React, { useEffect, useState } from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import COInlineMessage from '@components/ringil3/COInlineMessage/COInlineMessage.tsx';
import SvgGrayInfo from '@components/ringil3/icons/GrayInfo.tsx';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import SvgDelete from '@icons/delete-trash.svg?react';

interface ComponentProps {
  visible: boolean;
  onHide: () => void;
  onConfirm: () => void;
  companyName: string;
  deleting: boolean;
  serverError?: string;
}

const ConfirmDeleteCompanyDialog: React.FC<ComponentProps> = ({ visible, onHide, onConfirm, companyName, deleting, serverError }) => {
  const { tr } = useTranslationLgs();
  const [typedName, setTypedName] = useState('');

  useEffect(() => {
    if (visible) setTypedName('');
  }, [visible]);

  const nameMatches = typedName.trim() === (companyName ?? '').trim();
  const canDelete = nameMatches && !deleting;

  return (
    <>
      <Dialog
        visible={visible}
        onHide={onHide}
        style={{ width: '480px' }}
        header={tr('confirmDeleteCompanyDialog.title', 'Delete company?')}
      >
        <div>
          <div className="mb-6">
            <COInlineMessage variant={'warning'} icon={<SvgGrayInfo />}>
              {tr(
                'confirmDeleteCompanyDialog.warning',
                'This permanently deletes the company, its users (database + Auth0) and its modules. This action cannot be undone.',
              )}
            </COInlineMessage>
          </div>

          <div className="mb-2">
            {tr('confirmDeleteCompanyDialog.typeToConfirm', 'Type the company name "{{companyName}}" to confirm:', {
              companyName,
            })}
          </div>
          <div className="mb-6">
            <InputText
              value={typedName}
              onChange={e => setTypedName(e.target.value)}
              disabled={deleting}
              className="w-full"
              autoFocus
            />
          </div>

          {serverError && (
            <div className="mb-6">
              <COInlineMessage variant={'error'}>{serverError}</COInlineMessage>
            </div>
          )}

          <div className="mb-2">
            <RingilButton
              icon={<SvgDelete />}
              label={tr('confirmDeleteCompanyDialog.confirm', 'Delete company')}
              onClick={onConfirm}
              mode={'filled'}
              disabled={!canDelete}
              loading={deleting}
            />
            <RingilButton
              label={tr('confirmDeleteCompanyDialog.cancel', 'Cancel')}
              onClick={onHide}
              mode={'text-only'}
              disabled={deleting}
            />
          </div>
        </div>
      </Dialog>
    </>
  );
};

export default ConfirmDeleteCompanyDialog;
